import caOrderApi from '../services/ca-order-api.service';

/**
 * Button wrapper component that handles e-gift resend functionality
 */
class EgiftResendButton {
  /**
   * Create a new EgiftResendButton instance
   * @param {HTMLElement} buttonElement - The button element to enhance
   * @param {string} lineItemId - The line item ID for the e-gift
   */
  constructor(buttonElement, lineItemId) {
    this.buttonElement = buttonElement;
    this.lineItemId = lineItemId;
    this.originalButtonText = buttonElement ? buttonElement.innerHTML : '';

    // SVG check icon for success state
    this.checkIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M9.31445 1L4.86074 7L2 4.43855" stroke="#262626" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  /**
   * Initialize the button wrapper by attaching event listeners
   */
  init() {
    if (!this.buttonElement) return;

    this.buttonElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.resendEgift();
    });

    return this; // Enable chaining
  }

  /**
   * Set loading state on the button
   */
  setLoadingState(isLoading) {
    if (!this.buttonElement) return;

    if (isLoading) {
      this.buttonElement.setAttribute('disabled', '');
      this.buttonElement.classList.add('is-loading');
    } else {
      this.buttonElement.removeAttribute('disabled');
      this.buttonElement.classList.remove('is-loading');
    }
  }

  /**
   * Set the button to success state with check icon and "Resent" text
   */
  setSuccessState() {
    if (!this.buttonElement) return;

    this.buttonElement.innerHTML = `${this.checkIcon} Resent`;

    // Reset to original state after 3 seconds
    setTimeout(() => {
      this.resetState();
    }, 3000);
  }

  /**
   * Reset button to its original state
   */
  resetState() {
    if (!this.buttonElement) return;
    this.buttonElement.innerHTML = this.originalButtonText;
  }

  /**
   * Add an event listener for resend events
   * @param {string} eventName - The event name to listen for
   * @param {Function} callback - The callback function
   */
  on(eventName, callback) {
    if (!this.buttonElement) return this;

    this.buttonElement.addEventListener(`egift-resend-${eventName}`, callback);
    return this; // Enable chaining
  }

  /**
   * Trigger an event on the button
   * @param {string} eventName - The event name
   * @param {object} detail - Event details
   */
  trigger(eventName, detail = {}) {
    if (!this.buttonElement) return;

    this.buttonElement.dispatchEvent(
      new CustomEvent(`egift-resend-${eventName}`, {
        detail,
        bubbles: true,
      })
    );
  }

  /**
   * Handle the e-gift resend operation
   */
  async resendEgift() {
    if (!this.lineItemId) return;

    this.setLoadingState(true);

    try {
      const result = await caOrderApi.resendEgift(this.lineItemId);

      if (result.status) {
        this.setLoadingState(false);
        this.setSuccessState();
        this.trigger('success', { result });
      } else {
        console.error('Error resending egift:', result.error || 'Unknown error');
        this.resetState();
        this.setLoadingState(false);
        this.trigger('error', { error: result.error || 'Unknown error' });
      }
    } catch (error) {
      console.error('Error resending egift:', error);
      this.resetState();
      this.setLoadingState(false);
      this.trigger('error', { error: error.message });
    }
  }
}

export default EgiftResendButton;
